import React from 'react'
import { ChevronLeft, SquarePen } from 'lucide-react'
import { cn } from 'lib/utils'
import PopupCloseButton from 'components/PopupCloseButton'

export interface Props {
	title: string
	subtitle?: string
	onBack?: () => void
	onCompose?: () => void
	onClose: () => void
	className?: string
	/** Desktop only: the bar doubles as the panel's drag handle. */
	isDraggable?: boolean
	isDragging?: boolean
	onPointerDown?: (e: React.PointerEvent) => void
	onPointerMove?: (e: React.PointerEvent) => void
	onPointerUp?: (e: React.PointerEvent) => void
	onPointerCancel?: (e: React.PointerEvent) => void
}

/**
 * One bar shared by all three views, so the title and controls stay put as the
 * user moves between the list, a thread, and compose.
 *
 * On desktop it is also the panel's drag handle. The pointer handlers are owned
 * by ChatPopup, which knows where the panel is; this component only surfaces
 * the grab affordance and keeps the title from being selected mid-drag.
 */
const ChatPopupHeaderBar = ({
	title,
	subtitle,
	onBack,
	onCompose,
	onClose,
	className = '',
	isDraggable = false,
	isDragging = false,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onPointerCancel,
}: Props) => (
	<div
		onPointerDown={onPointerDown}
		onPointerMove={onPointerMove}
		onPointerUp={onPointerUp}
		onPointerCancel={onPointerCancel}
		className={cn(
			'flex shrink-0 items-center gap-1 border-b border-gray-200 px-2 py-2',
			'pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-2',
			// select-none only while draggable, or a drag across the bar leaves
			// the title highlighted.
			isDraggable && 'select-none',
			isDraggable && (isDragging ? 'cursor-grabbing' : 'cursor-grab'),
			className,
		)}
	>
		<div className="flex w-9 shrink-0 justify-start">
			{onBack && (
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to conversations"
					className="grid h-9 w-9 place-items-center rounded-md text-[#0080fe] transition-colors hover:bg-gray-100"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
			)}
		</div>

		<div className="min-w-0 flex-1 text-center">
			<h2 className="m-0 truncate text-[16px] font-semibold text-gray-900">{title}</h2>
			{subtitle && <p className="m-0 truncate text-[11px] text-gray-400">{subtitle}</p>}
		</div>

		<div className="flex shrink-0 items-center gap-0.5">
			{onCompose && (
				<button
					type="button"
					onClick={onCompose}
					aria-label="New message"
					className="grid h-9 w-9 place-items-center rounded-md text-[#0080fe] transition-colors hover:bg-gray-100"
				>
					<SquarePen className="h-[18px] w-[18px]" />
				</button>
			)}
			<PopupCloseButton onClose={onClose} ariaLabel="Close messages" className="-mr-1.5" />
		</div>
	</div>
)

export default ChatPopupHeaderBar
